import { useMemo } from "react"
import { CostCalculator } from "~/helpers/costCalculator"
import { type ResultType } from "~/react/components/multi_domain_search/result"
import { type TabSelected } from "~/react/types/tabSelected"

interface UseRevealCostParams {
  selectedIds: number[]
  tabSelected: TabSelected
  results: ResultType[]
  allPeopleResults: ResultType[]
  decisionMakersResults: ResultType[]
  genericResults: ResultType[]
  numberOfCreditsLeft: number
}

interface UseRevealCostReturn {
  numberOfEmailsToReveal: number
  numberOfCreditsNeeded: number
  neededCredits: number
  revealAllOperationCost: number
}

export function useRevealCost({
  selectedIds,
  tabSelected,
  results,
  allPeopleResults,
  decisionMakersResults,
  genericResults,
  numberOfCreditsLeft,
}: UseRevealCostParams): UseRevealCostReturn {
  return useMemo(() => {
    let numberOfEmailsToReveal = 0

    if (selectedIds.length > 0) {
      numberOfEmailsToReveal = results.filter(
        (result) => selectedIds.includes(result.id) && !result.revealed,
      ).length
    } else {
      numberOfEmailsToReveal =
        tabSelected === "all_people"
          ? allPeopleResults.filter((result) => !result.revealed).length
          : tabSelected === "decision_makers"
            ? decisionMakersResults.filter((result) => !result.revealed).length
            : new Set(genericResults.map((result) => result.domain)).size
    }

    const numberOfCreditsNeeded = CostCalculator.calculate(
      "search",
      numberOfEmailsToReveal,
    )
    let neededCredits = numberOfCreditsNeeded - numberOfCreditsLeft
    if (neededCredits < 0) {
      neededCredits = 0
    }
    const revealAllOperationCost =
      CostCalculator.calculateVisualRepresentation(
        "search",
        numberOfEmailsToReveal,
      )

    return {
      numberOfEmailsToReveal,
      numberOfCreditsNeeded,
      neededCredits,
      revealAllOperationCost,
    }
  }, [
    selectedIds,
    tabSelected,
    results,
    allPeopleResults,
    decisionMakersResults,
    genericResults,
    numberOfCreditsLeft,
  ])
}

